import LeadData from "../../../../model/lead/LeadData";
import {FormField, OptionalFormField, RequiredFormField} from "../../../../validation/FormField";
import FormValidation from "../../../../validation/FormValidation";
import {none, Option, option} from "ts-option";
import SiteData from "../../../../model/site/SiteData";
import Address from "../../../../model/address/Address";
import BaseFormController from "../../../../validation/BaseFormController";
import TrackingData from "../../../../model/lead/TrackingData";
import MentionUtils from "../../../../utils/MentionUtils";
import FormValidators from "../../../../validation/FormValidators";
import moment = require("moment");

const uuidv4 = require("uuid/v4");

/**
 * Lead form data.
 */
export default class LeadForm extends BaseFormController<LeadForm> {
	private constructor(formId: string,
		public readonly address: OptionalFormField<Address>,
		public readonly principalContactId: RequiredFormField<string>,
		public readonly extendedDescription: OptionalFormField<string>,
		public readonly categories: OptionalFormField<string[]>,
		public readonly fieldWithFocus: FormField<any>,
		public readonly acquisitionChannelId: RequiredFormField<string>,
		public readonly watchers: OptionalFormField<string[]>,
		public readonly appointmentDate: FormField<moment.Moment>,
		public readonly leadType: RequiredFormField<string>,
		public readonly campaign: OptionalFormField<string>,
		public readonly source: OptionalFormField<string>,
		public readonly adGroup: OptionalFormField<string>,
		public readonly landingPage: OptionalFormField<string>,
		public readonly url: OptionalFormField<string>,
		public readonly agentId: RequiredFormField<string>) {
		super(fieldWithFocus, formId);
	}

	private static createLeadForm({
		address = none,
		principalContactId = none,
		extendedDescription = none,
		categories = none,
		acquisitionChannelId = none,
		watchers = none,
		appointmentDate = none,
		leadType = none,
		campaign = none,
		source = none,
		adGroup = none,
		landingPage = none,
		url = none,
		agentId = none
	}) {
		const addressField = OptionalFormField.create<Address>(address);
		return new LeadForm(
			uuidv4(),
			addressField,
			RequiredFormField.create<string>(principalContactId, "Principal Contact is required"),
			OptionalFormField.create<string>(extendedDescription),
			OptionalFormField.create<string[]>(categories),
			addressField,
			RequiredFormField.create<string>(acquisitionChannelId, "Acquisition channel is required"),
			OptionalFormField.create<string[]>(watchers),
			OptionalFormField.create<moment.Moment>(appointmentDate),
			RequiredFormField.create<string>(leadType, "Lead type is required",
				[FormValidators.nonEmptyString("Lead type is required")]),
			OptionalFormField.create<string>(campaign),
			OptionalFormField.create<string>(source),
			OptionalFormField.create<string>(adGroup),
			OptionalFormField.create<string>(landingPage),
			OptionalFormField.create<string>(url),
			RequiredFormField.create<string>(agentId, "Agent is required")
		);
	}

	static createEmpty(agentId: Option<string>): LeadForm {
		return LeadForm.createLeadForm({agentId})
	}

	onValid(callback: (lead: LeadData,
		site: Option<SiteData>,
		watchers: string[],
		agentId: Option<string>) => void): void {
		const mainDataAreValid = FormValidation
			.validateFields([
					this.address,
					this.principalContactId,
					this.extendedDescription,
					this.categories,
					this.acquisitionChannelId,
					this.agentId,
					this.leadType
				],
				() => {}).isValid;
		if (mainDataAreValid) {
			const principalContact = this.principalContactId.getValue();
			const extendedDescription = this.extendedDescription.getOptionalValue();
			const desc = extendedDescription.map(dr => MentionUtils.getPlainText(dr, true));
			const categories = this.categories.getOptionalValue().getOrElse([]);
			const siteData = this.address.getOptionalValue().map(address => new SiteData(address));
			const acquisitionChannelId = this.acquisitionChannelId.getOptionalValue();
			const leadTypeId = this.leadType.getValue();
			const leadTracking =
				(this.campaign.getValue().isDefined ||
					this.source.getValue().isDefined ||
					this.adGroup.getValue().isDefined ||
					this.landingPage.getValue().isDefined ||
					this.url.getValue().isDefined)
					? option(new TrackingData(this.campaign.getValue(), this.source.getValue(), this.adGroup.getValue(),
					this.landingPage.getValue(), this.url.getValue()))
					: none;

			const leadData = new LeadData(none, principalContact, desc, extendedDescription, categories,
				acquisitionChannelId, leadTypeId, leadTracking, false);
			callback(leadData, siteData, this.watchers.getValue().getOrElse([]), this.agentId.getOptionalValue());
		}
	}

	copy({
		formId = this.formId,
		address = this.address,
		principalContactId = this.principalContactId,
		extendedDescription = this.extendedDescription,
		categories = this.categories,
		fieldWithFocus = this.fieldWithFocus,
		acquisitionChannelId = this.acquisitionChannelId,
		watchers = this.watchers,
		appointmentDate = this.appointmentDate,
		leadType = this.leadType,
		campaign = this.campaign,
		source = this.source,
		adGroup = this.adGroup,
		landingPage = this.landingPage,
		url = this.url,
		agentId = this.agentId
	}) {
		return new LeadForm(formId,
			address,
			principalContactId,
			extendedDescription,
			categories,
			fieldWithFocus,
			acquisitionChannelId,
			watchers,
			appointmentDate,
			leadType,
			campaign,
			source,
			adGroup,
			landingPage,
			url,
			agentId
		);
	}

	fields(): FormField<any> [] {
		return [this.address, this.principalContactId, this.extendedDescription, this.agentId];
	}
}